import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, downloadExport } from '../../api/client';

export function BackupCard() {
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');

  const download = async (kind) => {
    setBusy(kind);
    setMessage('');
    try {
      await downloadExport(kind === 'full' ? '/admin/backup' : '/admin/export');
      setMessage('Backup downloaded. Keep it somewhere safe (not only on this computer).');
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="admin-settings-card admin-safety-card">
      <h3><i className="fas fa-shield-alt" aria-hidden="true" /> Full backup</h3>
      <p>
        One zip with the whole database <em>and</em> every uploaded image. Download one before every website update.
      </p>
      <div className="admin-settings-actions">
        <button type="button" className="btn btn-primary" onClick={() => download('full')} disabled={!!busy}>
          <i className="fas fa-file-archive" aria-hidden="true" /> {busy === 'full' ? 'Packing zip…' : 'Download full backup (.zip)'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => download('json')} disabled={!!busy}>
          {busy === 'json' ? 'Preparing…' : 'Content only (.json)'}
        </button>
      </div>
      {message && <p className="admin-form-hint">{message}</p>}
    </div>
  );
}

export function MediaHealthCard({ compact = false }) {
  const [health, setHealth] = useState(null);
  const [error, setError] = useState('');
  const [registering, setRegistering] = useState(false);

  const load = useCallback(() => {
    setError('');
    return apiGet('/admin/media-health').then(setHealth).catch((err) => setError(err.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  const register = async () => {
    setRegistering(true);
    try {
      await apiPost('/admin/media-health/register');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRegistering(false);
    }
  };

  const missing = health?.missing || [];
  const ok = health && missing.length === 0;

  return (
    <div className="admin-settings-card admin-safety-card">
      <h3>
        <i className={`fas ${ok ? 'fa-check-circle is-ok' : 'fa-image'}`} aria-hidden="true" /> Image health
        <button type="button" className="admin-icon-btn admin-safety-card__refresh" onClick={load} title="Check again">
          <i className="fas fa-sync-alt" aria-hidden="true" />
        </button>
      </h3>
      {error && <p className="admin-message admin-message--error">{error}</p>}
      {!health && !error && <p>Checking uploaded images…</p>}
      {health && (
        <>
          <div className="admin-stat-chips">
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{health.filesOnDisk}</span>
              <span className="admin-stat-label">Files on server</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--green">
              <span className="admin-stat-value">{health.referenced}</span>
              <span className="admin-stat-label">Used by content</span>
            </div>
            <div className={`admin-stat-chip ${missing.length ? 'admin-stat-chip--red' : ''}`}>
              <span className="admin-stat-value">{missing.length}</span>
              <span className="admin-stat-label">Missing</span>
            </div>
          </div>
          {missing.length > 0 ? (
            <ul className="admin-safety-missing">
              {missing.slice(0, compact ? 3 : 20).map((m) => (
                <li key={m.file}>
                  <code>{m.file}</code>
                  <span>used in {m.usedIn.join(', ')}</span>
                </li>
              ))}
              {compact && missing.length > 3 && <li>…and {missing.length - 3} more (see Settings)</li>}
            </ul>
          ) : (
            <p className="admin-form-hint">Every image the site uses exists in the uploads folder.</p>
          )}
          {missing.length > 0 && (
            <p className="admin-form-hint">Re-upload missing images in Media, then pick them again where they are used.</p>
          )}
          {!compact && health.untracked?.length > 0 && (
            <div className="admin-settings-actions">
              <button type="button" className="btn btn-secondary" onClick={register} disabled={registering}>
                {registering ? 'Adding…' : `Add ${health.untracked.length} untracked file(s) to Media library`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function AiStatusCard() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    apiGet('/admin/ai/status').then(setStatus).catch(() => setStatus({ configured: false }));
  }, []);

  return (
    <div className="admin-settings-card admin-safety-card">
      <h3><i className="fas fa-magic" aria-hidden="true" /> AI writer</h3>
      {!status ? (
        <p>Checking…</p>
      ) : status.configured ? (
        <p className="admin-status-row">
          <span className="admin-status-dot admin-status-dot--ok" />
          Connected to <strong>{status.provider}</strong> ({status.model})
        </p>
      ) : (
        <>
          <p className="admin-status-row">
            <span className="admin-status-dot admin-status-dot--unknown" /> Not set up
          </p>
          <p className="admin-form-hint">
            Add an API key in <Link to="/admin/settings">Settings → AI writer</Link>.
          </p>
        </>
      )}
      <p className="admin-form-hint">
        Used by Reviews → AI writer and Tool Pages → Generate with AI. <Link to="/admin/settings">Change provider or model</Link>.
      </p>
    </div>
  );
}
